import { useApps } from '@auth/useApps'
import { mobileQuery } from '@globals/themes/themeConstants'
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { T } from '@transifex/react'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import ChecklistSection from './ChecklistSection'
import PlatformSetupProgress from './PlatformSetupProgress'
import { useDashboardData } from './useDashboardData'

const titleStyle = {
  margin: 0,
  [mobileQuery]: {
    margin: 0,
  },
}

type DashboardContentProps = {
  onDismissGuide?: () => void
  isEmbedded?: boolean
  onAlreadyOnActionUrl?: () => void
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  onDismissGuide,
  isEmbedded = false,
  onAlreadyOnActionUrl,
}) => {
  const { sections, progress, handleItemToggle, dismissGuide, isDismissed } = useDashboardData()
  const isMobile = useMediaQuery(mobileQuery)
  const location = useLocation()
  const [hasAppPermission] = useApps()

  // Determine if current route matches a checklist item (top-level or sub-item)
  const expandedItemKey = useMemo(() => {
    const path = location.pathname
    for (const section of sections) {
      for (const item of section.items) {
        const match = item.subItems?.find(sub => sub.actionUrl === path)
        if (match) return item.key
      }
    }
    return undefined
  }, [location.pathname, sections])

  // Scroll the matched item into view when present
  // Timeout-based scroll for reliability
  useEffect(() => {
    if (!expandedItemKey) return
    const timer = window.setTimeout(() => {
      const el = document.getElementById(`checklist-item-${expandedItemKey}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      }
    }, 250)
    return () => clearTimeout(timer)
  }, [expandedItemKey])

  const handleDismissGuide = () => {
    if (onDismissGuide) {
      onDismissGuide()
    } else {
      dismissGuide()
    }
  }

  if (isDismissed && !isEmbedded) {
    return null
  }

  return (
    <Card sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isMobile ? <Box /> : <BallotOutlinedIcon />}
          <Typography variant="h2" sx={titleStyle}>
            <T _str="Platform setup" />
          </Typography>
        </Stack>
        <Button id="btnMarkComplete" variant="text" onClick={handleDismissGuide}>
          <T _str="Dismiss guide" />
        </Button>
      </Stack>
      <Typography variant="body1" sx={{ padding: 1 }}>
        <T _str="Complete the essentials to get your platform ready to launch, start with what matters most to your teams." />
      </Typography>
      <PlatformSetupProgress progress={progress} />
      {/* Checklist Sections */}
      {sections.map(section => (
        <ChecklistSection
          key={section.key}
          section={section}
          onItemToggle={handleItemToggle}
          onAlreadyOnActionUrl={onAlreadyOnActionUrl}
          expandedItemKey={expandedItemKey}
          hasAppPermission={hasAppPermission}
        />
      ))}
    </Card>
  )
}

export default DashboardContent
